import { useEffect, useState, useRef } from 'react';

/**
 * Custom React hook that manages the "sticky" state of an element by utilizing the
 * IntersectionObserver API.
 *
 * The hook observes a sentinel element to detect when the sticky element should be considered
 * "stuck" or not, based on whether the sentinel is intersecting the viewport or not.
 *
 * @param {React.RefObject} stickyRef - A reference to the DOM element that needs to become sticky
 * when scrolled past.
 *
 * @returns {Object} - Returns an object containing:
 *   - {boolean} isSticky: A boolean indicating if the sticky element is "stuck" (i.e., not
 *     intersecting the sentinel).
 *   - {React.RefObject} sentinelRef: A reference to a sentinel element used to detect when the
 *     sticky state should change.
 *
 * @example
 * const { isSticky, sentinelRef } = useSticky(stickyElementRef);
 *
 * <div ref={sentinelRef} />
 * <div ref={stickyElementRef} className={isSticky ? 'sticky' : ''} />
 */
function useSticky(stickyRef) {
  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const stickyElement = stickyRef.current;

    if (!sentinel || !stickyElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When the sentinel is not intersecting, the sticky element is stuck
        setIsSticky(!entry.isIntersecting);
      },
      {
        threshold: [1.0],
      },
    );

    observer.observe(sentinel);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
    };
  }, [stickyRef]);

  return { isSticky, sentinelRef };
}

export default useSticky;
